import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import supportApi from "../api/supportApi";

const reasons = [
  "I no longer use CaloriesPal",
  "I created another account",
  "I have a privacy concern",
  "I want my recipes and dashboards removed",
  "Other",
];

const getErrorMessage = (err, fallback) => {
  const data = err?.data || err?.response?.data;
  if (data?.message) return data.message;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) return data.errors[0].msg;
  return fallback;
};

const DeleteAccountRequest = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    username: "",
    reason: "",
    details: "",
  });
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("");
    setError("");

    if (!form.email || !form.reason) {
      setError("Please enter your account email and a reason.");
      return;
    }

    setIsLoading(true);
    try {
      const data = await supportApi.requestAccountDeletion(form);
      setStatus(data.message);
      setForm({
        name: "",
        email: "",
        username: "",
        reason: "",
        details: "",
      });
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "We could not send your request. Please email contact@caloriespal.com directly."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Delete Account Request | CaloriesPal</title>
        <meta
          name="description"
          content="Request deletion of your CaloriesPal account, recipes, dashboards, and related user data."
        />
        <link rel="canonical" href="https://caloriespal.com/delete-account-request" />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          py: { xs: 4, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <Button
              component={RouterLink}
              to="/"
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Back to home
            </Button>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
                gap: 3,
                alignItems: "stretch",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  bgcolor: "#0f172a",
                  color: "white",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Typography variant="overline" sx={{ color: "#bef264" }}>
                  Account support
                </Typography>
                <Typography variant="h3" component="h1" fontWeight={900} sx={{ mt: 1 }}>
                  Delete Account Request
                </Typography>
                <Typography sx={{ mt: 3, color: "rgba(255,255,255,0.76)", lineHeight: 1.8 }}>
                  Use this form to request deletion of your CaloriesPal account and
                  related account data. We verify account ownership before deleting
                  records to protect users from unauthorized deletion requests.
                </Typography>
                <Stack spacing={2} sx={{ mt: 4 }}>
                  <Typography>What happens next:</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>
                    1. We receive your request by email.
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>
                    2. We verify the account email or ask for more information.
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>
                    3. We remove or anonymize eligible account data.
                  </Typography>
                </Stack>
              </Paper>

              <Paper
                component="form"
                onSubmit={handleSubmit}
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 24px 70px rgba(15,23,42,0.08)",
                }}
              >
                <Stack spacing={2.2}>
                  <Box>
                    <Typography variant="h5" fontWeight={800}>
                      Request deletion
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      Enter the email connected to your CaloriesPal account.
                    </Typography>
                  </Box>

                  {status && <Alert severity="success">{status}</Alert>}
                  {error && <Alert severity="error">{error}</Alert>}

                  <TextField
                    label="Full name"
                    value={form.name}
                    onChange={updateField("name")}
                    fullWidth
                  />
                  <TextField
                    label="Account email"
                    type="email"
                    value={form.email}
                    onChange={updateField("email")}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Username, if known"
                    value={form.username}
                    onChange={updateField("username")}
                    fullWidth
                  />
                  <TextField
                    select
                    label="Reason"
                    value={form.reason}
                    onChange={updateField("reason")}
                    required
                    fullWidth
                  >
                    {reasons.map((reason) => (
                      <MenuItem value={reason} key={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Additional details"
                    value={form.details}
                    onChange={updateField("details")}
                    multiline
                    minRows={4}
                    fullWidth
                    placeholder="Add anything that helps us verify the correct account."
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{ py: 1.4, borderRadius: 2 }}
                  >
                    {isLoading ? "Sending request..." : "Submit delete request"}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/privacy-policy"
                    sx={{ textTransform: "none", alignSelf: "flex-start" }}
                  >
                    Read Privacy Policy
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default DeleteAccountRequest;
